import React from 'react';
import { Heart, Target, Award, Users } from 'lucide-react';

const OverOns = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1514125739542-4957e1daf47c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxtYXVyaXRpdXMlMjBvY2VhbnxlbnwwfHx8fDE3NzYxNzQ1Nzd8MA&ixlib=rb-4.1.0&q=85"
            alt="Over Ons"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/85 via-blue-900/75 to-blue-800/70" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Over Nikita's Reizen op Maat
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Passie voor reizen en oog voor detail
          </p>
        </div>
      </section>

      {/* Ontmoet Nikita */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Foto */}
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src="https://customer-assets.emergentagent.com/job_maatwerk-vakantie/artifacts/dj460mff_WhatsApp%20Image%202026-04-08%20at%2019.02.33%20%281%29.jpeg"
                      alt="Nikita Lemmens"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-blue-300/20 rounded-full blur-3xl" />
                  <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-400/20 rounded-full blur-3xl" />
                </div>
              </div>

              {/* Verhaal */}
              <div className="order-1 lg:order-2">
                <div className="inline-block px-4 py-2 bg-blue-100 rounded-full text-blue-900 font-medium mb-6">
                  Ontmoet Nikita
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">
                  Van Reisdroom tot Werkelijkheid
                </h2>
                <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                  <p>
                    Hoi, ik ben <span className="font-semibold text-blue-900">Nikita Lemmens</span>, en reizen is mijn absolute passie. Wat begon als een persoonlijke liefde voor avontuur en nieuwe culturen ontdekken, is uitgegroeid tot mijn missie: andere mensen helpen om hun droomreis werkelijkheid te maken.
                  </p>
                  <p>
                    Ik geloof dat elke reis meer is dan alleen een vakantie – het is een kans om jezelf te verrijken, nieuwe perspectieven te ontdekken en herinneringen te creëren die een leven lang meegaan. Die magie wil ik met jou delen.
                  </p>
                  <p>
                    Bij <span className="font-semibold text-blue-900">Nikita's Reizen op Maat</span> draait alles om jouw unieke wensen. Geen standaard pakketten, maar reizen die perfect bij jou passen. Of je nu droomt van een tropisch paradijs, een cultureel avontuur of een combinatie van beide – ik help je om precies die reis samen te stellen die jij voor ogen hebt.
                  </p>
                  <p className="text-blue-900 font-semibold italic">
                    "Jouw reis, jouw verhaal, perfect geregeld – daar sta ik voor!"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij voor je regelen */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">
                Wat wij voor je regelen
              </h2>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
                Bij <span className="font-semibold text-blue-900">Nikita's Reizen op Maat</span> draait alles om een reis die volledig aansluit op jouw wensen. Of je nu kiest voor een avontuurlijke rondreis, een ontspannen strandvakantie of een unieke bestemming buiten de gebaande paden, wij stellen jouw reis met zorg samen.
              </p>
            </div>

            <div className="text-center mb-8">
              <p className="text-lg text-gray-700 font-medium">Wij regelen onder andere:</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {[
                {
                  emoji: '🏨',
                  titel: 'Accommodaties',
                  beschrijving: 'Van sfeervolle boutique hotels tot luxe resorts.'
                },
                {
                  emoji: '🚐',
                  titel: 'Transfers',
                  beschrijving: 'Zodat je zorgeloos van bestemming naar bestemming reist.'
                },
                {
                  emoji: '🌍',
                  titel: 'Activiteiten & Excursies',
                  beschrijving: 'Die passen bij jouw interesses, zoals natuur, cultuur, avontuur of ontspanning.'
                },
                {
                  emoji: '🗺️',
                  titel: 'Een Persoonlijke Reisroute',
                  beschrijving: 'Logisch opgebouwd en afgestemd op jouw reistempo en budget.'
                }
              ].map((item) => (
                <div
                  key={item.titel}
                  className="flex items-start gap-5 p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="text-4xl flex-shrink-0">{item.emoji}</div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">{item.titel}</h3>
                    <p className="text-gray-700 leading-relaxed">{item.beschrijving}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-8 md:p-10 text-center text-white">
              <p className="text-lg md:text-xl leading-relaxed">
                Onze reizen zijn volledig op maat samen te stellen. Je kiest zelf hoe uitgebreid je reis wordt en wij zorgen voor een onvergetelijke ervaring, met <span className="font-semibold text-yellow-300">persoonlijke service van begin tot eind</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onze Missie */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">
                Mijn Belofte Aan Jou
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Ik geloof dat <span className="font-semibold text-blue-900">geen enkele reis hetzelfde moet zijn</span>. Daarom bied ik persoonlijke service en maatwerk voor elke klant.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Met passie voor reizen en oog voor detail zorg ik ervoor dat jouw reis niet alleen goed geregeld is, maar ook echt bijzonder wordt. Elke bestemming heeft zijn eigen verhaal, en ik help jou om jouw persoonlijke reisverhaal te schrijven.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Waarom Voor Ons Kiezen */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
              Waarom Kiezen Voor Ons?
            </h2>
            <p className="text-xl text-gray-600">
              Dit maakt ons uniek
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: Heart,
                titel: "Persoonlijk Advies",
                beschrijving: "Elk reisplan wordt met liefde en zorg samengesteld. Wij nemen de tijd om jouw wensen echt te begrijpen en vertalen deze naar een perfecte reis."
              },
              {
                icon: Target,
                titel: "Reizen Volledig op Maat",
                beschrijving: "Geen standaard pakketten, maar reizen die 100% passen bij jouw persoonlijkheid, budget en reisstijl. Van budget tot luxe, alles is mogelijk."
              },
              {
                icon: Award,
                titel: "Flexibel & Snel Geregeld",
                beschrijving: "Snelle service zonder concessies aan kwaliteit. Flexibel inspelen op jouw wensen en veranderingen, zodat jouw reis perfect blijft."
              },
              {
                icon: Users,
                titel: "Geschikt voor Elk Budget",
                beschrijving: "Of je nu droomt van een luxe vakantie of een budgetvriendelijk avontuur, wij maken het mogelijk binnen jouw financiële kader."
              }
            ].map((item) => (
              <div
                key={item.titel}
                className="group p-8 rounded-2xl bg-white border border-blue-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-3">{item.titel}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.beschrijving}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Onze Aanpak */}
      <section className="py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Onze Aanpak
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed mb-12">
              Wij werken persoonlijk met elke klant om een unieke reiservaring te creëren. 
              Van het eerste contact tot aan je thuiskomst, wij staan voor je klaar.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {[
                {
                  titel: "Luisteren",
                  beschrijving: "We nemen de tijd om jouw wensen en dromen te begrijpen"
                },
                {
                  titel: "Creëren",
                  beschrijving: "We stellen een perfect reisplan samen dat bij jou past"
                },
                {
                  titel: "Ondersteunen",
                  beschrijving: "We blijven beschikbaar voor vragen en aanpassingen"
                }
              ].map((item) => (
                <div key={item.titel} className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <h3 className="text-2xl font-bold mb-3">{item.titel}</h3>
                  <p className="text-blue-200">{item.beschrijving}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">
              Klaar om Je Droomreis te Plannen?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Laten we samen werken aan jouw perfecte reiservaring
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-800 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            >
              Neem Contact Op
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OverOns;
